import { ChangeDetectionStrategy, Component, Input, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BuildProfileDialogComponent } from '@portal-core/builds/components/build-profile-dialog/build-profile-dialog.component';
import { AnyBuildId } from '@portal-core/builds/constants/any-build-id.constant';
import { LatestBuildId } from '@portal-core/builds/constants/latest-build-id.constant';
import { Build } from '@portal-core/builds/models/build.model';
import { StringService } from '@portal-core/general/services/string.service';
import { ProcessAttemptStatus } from '@portal-core/processes/enums/process-attempt-status.enum';
import { AvatarBase } from '@portal-core/ui/avatar/util/avatar.base';

@Component({
  selector: 'mc-build-avatar',
  templateUrl: './build-avatar.component.html',
  styleUrls: ['./build-avatar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BuildAvatarComponent extends AvatarBase {
  @Input() build?: Build;
  @Input() labelAsLatest?: boolean = false;
  @Input() showCompletedDate?: boolean = false;

  get any(): boolean {
    return this.build?.Id === AnyBuildId;
  }

  get latest(): boolean {
    return this.build?.Id === LatestBuildId;
  }

  get name(): string {
    let name = '';

    if (this.latest || this.labelAsLatest) {
      name = 'Latest Build';
    } else if (this.any) {
      name = 'Any Build';
    } else if (this.build) {
      name = `${this.build.Id}`;
    }

    const endedOn = this.build?.Process?.LastProcessAttempt?.EndedOn;
    if (this.showCompletedDate && name && endedOn) {
      name += ` - ${this.stringService.formatDate(endedOn, 'MMM dd, yyyy h:mm a')}`;
    }

    return name;
  }

  get tooltip(): string {
    let tooltip = '';

    if (this.latest) {
      tooltip = 'Latest Build';
    } else if (this.any) {
      tooltip = 'Any Build';
    } else if (this.build) {
      tooltip = `Build ${this.build.Id}`;

      const attempt = this.build.Process?.LastProcessAttempt;
      const endTime = attempt?.EndedOn;
      switch (attempt?.Status) {
      case ProcessAttemptStatus.Complete:
        if (endTime) {
          tooltip += ` completed ${this.stringService.formatDate(endTime, 'MMM dd, yyyy h:mm a')}`;
        }
        break;
      case ProcessAttemptStatus.Canceled:
        if (endTime) {
          tooltip += ` canceled ${this.stringService.formatDate(endTime, 'MMM dd, yyyy h:mm a')}`;
        }
        break;
      case ProcessAttemptStatus.Failed:
      case ProcessAttemptStatus.Aborted:
        if (endTime) {
          tooltip += ` failed ${this.stringService.formatDate(endTime, 'MMM dd, yyyy h:mm a')}`;
        }
        break;
      default:
        tooltip += ` queued ${this.stringService.formatDate(this.build.CreatedOn, 'MMM dd, yyyy h:mm a')}`;
        break;
      }
    }

    return tooltip;
  }

  constructor(private dialog: MatDialog, private stringService: StringService) {
    super();
  }

  onAvatarClicked() {
    this.openBuildProfileDialog();
  }

  hasAvatarImage(): boolean {
    return false;
  }

  hasPermission(): boolean {
    return true;
  }

  isDeleted(): boolean {
    return false;
  }

  isEmpty(): boolean {
    return !this.build && !this.labelAsLatest;
  }

  private openBuildProfileDialog() {
    this.dialog.open(BuildProfileDialogComponent, {
      ...BuildProfileDialogComponent.DialogConfig,
      data: {
        buildId: this.build.Id,
      }
    });
  }
}
